import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  LinearProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  HourglassEmpty as PendingIcon,
} from '@mui/icons-material';
import { NotificationType, NotificationPriority } from '../../../types/user';
import { NotificationService } from '../../../services/notificationService';
import { useAuth } from '../../../contexts/AuthContext';

interface TemplateTestResult {
  type: NotificationType;
  status: 'pending' | 'success' | 'error';
  error?: string;
}

/**
 * TemplateTestRunner - Batch test all notification templates
 */
const TemplateTestRunner: React.FC = () => {
  const { authState } = useAuth();
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState<TemplateTestResult[]>([]);
  const [progress, setProgress] = useState(0);

  const allTypes: NotificationType[] = [
    'welcome', 'approval_granted', 'approval_denied', 'system_announcement',
    'feature_announcement', 'maintenance_scheduled', 'admin_broadcast',
  ];

  const runAllTests = async () => {
    if (!authState.user) return;

    setRunning(true);
    setResults([]);
    setProgress(0);

    const testResults: TemplateTestResult[] = [];

    for (let i = 0; i < allTypes.length; i++) {
      const type = allTypes[i];

      try {
        await NotificationService.createNotification({
          userId: authState.user.uid,
          type,
          priority: NotificationPriority.LOW,
          data: { test: true, batchTest: true },
        });

        testResults.push({ type, status: 'success' });
      } catch (error: any) {
        testResults.push({ type, status: 'error', error: error.message });
      }

      setProgress(((i + 1) / allTypes.length) * 100);
      setResults([...testResults]);

      // Small delay between tests
      await new Promise(resolve => setTimeout(resolve, 500));
    }

    setRunning(false);
  };

  const successCount = results.filter(r => r.status === 'success').length;
  const errorCount = results.filter(r => r.status === 'error').length;

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Template Batch Tester
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Test all notification templates at once to verify they work correctly
      </Typography>

      <Button
        variant="contained"
        startIcon={<PlayIcon />}
        onClick={runAllTests}
        disabled={running || !authState.user}
        fullWidth
        sx={{ mb: 3 }}
      >
        {running ? 'Running Tests...' : `Run All Tests (${allTypes.length} templates)`}
      </Button>

      {running && (
        <Box sx={{ mb: 3 }}>
          <LinearProgress variant="determinate" value={progress} />
          <Typography variant="caption" color="text.secondary" textAlign="center" display="block" mt={1}>
            {Math.round(progress)}% Complete
          </Typography>
        </Box>
      )}

      {results.length > 0 && (
        <>
          <Box display="flex" gap={2} mb={2}>
            <Chip label={`Success: ${successCount}`} color="success" size="small" />
            <Chip label={`Failed: ${errorCount}`} color="error" size="small" />
          </Box>

          <List dense>
            {results.map((result, index) => (
              <ListItem key={index}>
                <ListItemIcon>
                  {result.status === 'success' ? (
                    <CheckIcon color="success" />
                  ) : result.status === 'error' ? (
                    <ErrorIcon color="error" />
                  ) : (
                    <PendingIcon color="disabled" />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={result.type.replace(/_/g, ' ').toUpperCase()}
                  secondary={result.error || 'Test passed'}
                />
              </ListItem>
            ))}
          </List>

          {!running && (
            <Alert severity={errorCount === 0 ? 'success' : 'warning'} sx={{ mt: 2 }}>
              Test complete: {successCount}/{results.length} templates working correctly
            </Alert>
          )}
        </>
      )}
    </Paper>
  );
};

export default TemplateTestRunner;
